"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Loader, MapPin, Phone, Mail, Linkedin, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { Link } from "@/components/common/Link";
export default function FormulaireDeConsultation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="formulaire-de-consultation" className="dark bg-background text-foreground py-24 px-4 md:px-8 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
      backgroundImage: 'linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)',
      backgroundSize: '60px 60px'
    }}>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
          <div ref={ref} className={cn("lg:col-span-5 space-y-12 transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium tracking-wider uppercase border border-primary/20">
                Contactez-nous
              </div>
              <h2 className="text-4xl md:text-5xl font-sans font-bold tracking-tight text-white leading-tight">
                Discutons de votre <span className="text-primary">projet</span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed border-l-2 border-primary/30 pl-6 !w-[525px]">Que vous soyez promoteur ou particulier, notre équipe vous accompagne de la conception à la livraison. Remplissez le formulaire pour nous décrire votre demande et nous reviendrons vers vous sous 24h pour une étude personnalisée.</p>
            </div>

            <div className="grid gap-8">
              <div className="flex items-start gap-5 group">
                <div className="flex-shrink-0 p-4 rounded-sm bg-card border border-border group-hover:border-primary/50 transition-colors duration-300">
                  <MapPin className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium text-white text-lg mb-1">Adresse</h3>
                  <p className="text-muted-foreground">20 Quai Pierre Scize, Lyon, Auvergne-Rhône-Alpes</p>
                </div>
              </div>

              <div className="flex items-start gap-5 group">
                <div className="flex-shrink-0 p-4 rounded-sm bg-card border border-border group-hover:border-primary/50 transition-colors duration-300">
                  <Phone className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium text-white text-lg mb-1">Téléphone</h3>
                  <p className="text-muted-foreground">+33 6 14 34 40 65</p>
                </div>
              </div>

              <div className="flex items-start gap-5 group">
                <div className="flex-shrink-0 p-4 rounded-sm bg-card border border-border group-hover:border-primary/50 transition-colors duration-300">
                  <Mail className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium text-white text-lg mb-1">Email</h3>
                  <p className="text-muted-foreground">contact@nsdb.fr</p>
                </div>
              </div>

              <div className="flex items-start gap-5 group">
                <div className="flex-shrink-0 p-4 rounded-sm bg-card border border-border group-hover:border-primary/50 transition-colors duration-300">
                  <Linkedin className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium text-white text-lg mb-1">LinkedIn</h3>
                  <Link to="https://www.linkedin.com/in/nicolas-sicard-9b927722a?lipi=urn%3Ali%3Apage%3Ad_flagship3_profile_view_base_contact_details%3BR5nHyU1mR9eIV3IkGdJ6Hg%3D%3D" newTab className="text-muted-foreground hover:text-primary transition-colors">Suivez notre profil</Link>
                </div>
              </div>
            </div>
          </div>

          <div className={cn("lg:col-span-7 bg-card/50 backdrop-blur-sm border border-border p-8 md:p-10 rounded-sm shadow-2xl transition-all duration-700 delay-200 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
            <div className="mb-8 pb-6 border-b border-border/50">
              <h3 className="text-2xl font-bold text-white">Formulaire de Consultation</h3>
              <p className="text-muted-foreground mt-2 text-sm">
                Les champs marqués d'un astérisque (*) sont obligatoires.
              </p>
            </div>

            <FormProvider formId="consultation_form_contact" sectionName="contact-section" email="contact@nsdb.fr" defaultValues={{
            nom_complet: "",
            email: "",
            telephone: "",
            objet_consultation: "",
            message: ""
          }} validationRules={{
            nom_complet: {
              required: true,
              minLength: 2,
              maxLength: 100
            },
            email: {
              required: true,
              pattern: "email"
            },
            telephone: {
              required: true,
              pattern: "phone"
            },
            objet_consultation: {
              required: true
            },
            message: {
              required: false
            }
          }} submitText="Envoyer la demande" successMessage="Votre demande a bien été envoyée. Notre équipe vous recontactera sous 24h." errorMessage="Une erreur est survenue lors de l'envoi. Veuillez réessayer.">
              <ConsultationForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-16 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-2 border border-primary/20">
          <ArrowRight className="size-8" />
        </div>
        <div className="space-y-3">
          <h3 className="text-2xl font-bold text-white">Message envoyé !</h3>
          <p className="text-muted-foreground max-w-md mx-auto leading-relaxed">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground h-12 px-8">
          Envoyer un autre message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="nom_complet" rules={{
        required: "Le nom complet est requis",
        minLength: {
          value: 2,
          message: "Minimum 2 caractères"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Nom complet *</FormLabel>
              <FormControl>
                <Input placeholder="Jean Dupont" {...field} className="bg-background/30 border-input focus:border-primary h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "L'adresse e-mail est requise",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Adresse e-mail invalide"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Adresse e-mail *</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jean@example.com" {...field} className="bg-background/30 border-input focus:border-primary h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="telephone" rules={{
        required: "Le numéro de téléphone est requis"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Téléphone *</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+33 6 12 34 56 78" {...field} className="bg-background/30 border-input focus:border-primary h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="objet_consultation" rules={{
        required: "Veuillez sélectionner un objet"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Objet de la consultation *</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-background/30 border-input focus:border-primary h-12">
                    <SelectValue placeholder="Sélectionnez un service" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Maîtrise d’Œuvre – Promoteur">Maîtrise d’Œuvre – Promoteur</SelectItem>
                  <SelectItem value="Maîtrise d’Œuvre – Particulier">Maîtrise d’Œuvre – Particulier</SelectItem>
                  <SelectItem value="Audit énergétique">Audit énergétique</SelectItem>
                  <SelectItem value="AMO Livraisons">AMO Livraisons</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription className="text-xs text-muted-foreground/80">
                Sélectionnez le service concerné.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Message / Description du projet</FormLabel>
            <FormControl>
              <Textarea placeholder="Décrivez votre projet, vos besoins, vos contraintes..." className="min-h-[160px] bg-background/30 border-input focus:border-primary resize-y p-4" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm animate-in fade-in slide-in-from-top-2">
          <p className="text-destructive text-sm flex items-center gap-2 font-medium">
            <span className="size-1.5 rounded-full bg-destructive inline-block" />
            {submitError}
          </p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[240px] bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-14 text-base shadow-lg shadow-primary/10 rounded-sm transition-all duration-300 hover:translate-y-[-2px]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Envoi en cours...
            </> : 'Envoyer la demande'}
        </Button>
      </div>
    </Form>;
}